import React from 'react';
import styled, { css } from 'styled-components';

interface InputProps {
  label?: string;
  type?: 'text' | 'email' | 'password' | 'number';
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

const Container = styled.div`
  width: 100%;
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  color: ${props => props.theme.colors.book.color6b};
`;

const Required = styled.span`
  color: #DF0707;
  font-size: ${props => props.theme.fontSize.xs};
`;

const StyledInput = styled.input<{ $hasError: boolean; $disabled: boolean }>`
  width: 100%;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border: 1px solid ${props => props.$hasError ? props.theme.colors.error[300] : props.theme.colors.border.de};
  border-radius: ${props => props.theme.borderRadius.lg};
  background-color: var(--bg-secondary);
  font-size: ${props => props.theme.fontSize.base};
  color: ${props => props.theme.colors.book.color2b};
  font-family: inherit;
  font-weight: ${props => props.theme.fontWeight.normal};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  height: 44px;
  outline: none;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? props.theme.colors.error[500] : props.theme.colors.book.color2b};
    background-color: var(--bg-secondary);
  }

  &:hover:not(:disabled) {
    border-color: ${props => props.$hasError ? props.theme.colors.error[400] : props.theme.colors.gray[300]};
  }

  ${props => props.$disabled && css`
    background-color: ${props.theme.colors.border.de};
    cursor: not-allowed;
  `}

  &::placeholder {
    color: ${props => props.theme.colors.gray[400]};
    font-weight: ${props => props.theme.fontWeight.normal};
  }
`;

const ErrorMessage = styled.p`
  margin-top: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.error[500]};
  font-weight: ${props => props.theme.fontWeight.medium};
`;

const Input: React.FC<InputProps> = ({
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  error,
  disabled = false,
  required = false,
}) => {
  const inputId = label ? label.toLowerCase().replace(/\s+/g, '-') : undefined;

  return (
    <Container>
      {label && (
        <Label htmlFor={inputId}>
          {label}
          {required && <Required>*</Required>}
        </Label>
      )}
      <StyledInput
        id={inputId}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        $hasError={!!error}
        $disabled={disabled}
      />
      {error && (
        <ErrorMessage>{error}</ErrorMessage>
      )}
    </Container>
  );
};

export default Input;
